import { useEffect, useState } from 'react';
import Workspace, {
  getWorkspaceFrameworkDisplayName,
  WorkspaceDefaultModule,
  WorkspaceFramework,
} from '../../../../types/Workspace';
import { getDefaultModuleOptions } from '../../../../utils/workspaceUtils';
import { MigrationStatusMapping, SpaceTasksMigration, TaskStatusCount } from './SpaceTasksMigration';
import { useGetTasks } from '../../../../serverState/tasks';
import { FrameworkCard } from '../../../../components/Cards/FrameworkCard';
import { Divider, Modal } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { BaseButton } from '../../../../components/UI/BaseButton';
import CustomTooltip from '../../../../components/UI/Tooltip/CustomTooltip';

export type SpaceSettingsFrameworkProps = {
  space?: Workspace;
  onSave: (
    framework: WorkspaceFramework,
    statusesMapping: MigrationStatusMapping,
    defaultModule?: WorkspaceDefaultModule
  ) => void;
  setIsChanged?: (isChanged: boolean) => void;
  isDisabled?: boolean;
};

export const SpaceSettingsFramework = ({ space, onSave, setIsChanged, isDisabled }: SpaceSettingsFrameworkProps) => {
  const [selectedFramework, setSelectedFramework] = useState<WorkspaceFramework>(
    space?.framework || WorkspaceFramework.scrum
  );
  const [selectedDefaultModule, setSelectedDefaultModule] = useState<WorkspaceDefaultModule>(
    space?.defaultModule || WorkspaceDefaultModule.backlog
  );
  const [isTaskMigrationMode, setisTaskMigrationMode] = useState(false);
  const [statusesMapping, setStatusesMapping] = useState<MigrationStatusMapping>({});
  const [statusesTaskCount, setStatusesTaskCount] = useState<TaskStatusCount>({});
  const [showSwitchFrameworkModal, setShowSwitchFrameworkModal] = useState(false);
  const tasksQuery = useGetTasks(space?.id || 0);

  const isDefaultModuleChanged = selectedDefaultModule !== (space?.defaultModule || WorkspaceDefaultModule.backlog);

  const handleUpdateFramework = (framework: WorkspaceFramework) => {
    setSelectedFramework(framework);
    setisTaskMigrationMode(framework != space?.framework);
    setIsChanged && setIsChanged(framework != space?.framework);

    if (framework === space?.framework) {
      setSelectedDefaultModule(space?.defaultModule || WorkspaceDefaultModule.backlog);
    } else {
      const validOptions = getDefaultModuleOptions(framework);
      if (!validOptions.includes(selectedDefaultModule)) {
        setSelectedDefaultModule(WorkspaceDefaultModule.backlog);
      }
    }
  };

  const handleDefaultModuleChange = (module: WorkspaceDefaultModule) => {
    setSelectedDefaultModule(module);
    const originalModule = space?.defaultModule || WorkspaceDefaultModule.backlog;
    setIsChanged?.(module !== originalModule || selectedFramework !== space?.framework);
  };

  useEffect(() => {
    let mapping: MigrationStatusMapping = {};
    switch (space?.framework) {
      case WorkspaceFramework.scrum:
        mapping = { planning: 'backlog', sprint: selectedFramework === WorkspaceFramework.none ? 'backlog' : 'kanban' };
        break;
      case WorkspaceFramework.kanban:
        mapping = { kanban: selectedFramework === WorkspaceFramework.none ? 'backlog' : 'sprint' };
        break;
      case WorkspaceFramework.none:
        mapping = { backlog: 'backlog' };
        break;
    }
    setStatusesMapping(mapping);
  }, [selectedFramework]);

  useEffect(() => {
    if (tasksQuery.data) {
      let planningTasksCount = 0;
      let sprintKanbanTasksCount = 0;

      tasksQuery.data.forEach((task) => {
        if (task.planningSessionId) planningTasksCount++;
        if (task.sprintId) sprintKanbanTasksCount++;
      });

      setStatusesTaskCount(
        space?.framework === WorkspaceFramework.kanban
          ? {
              kanban: sprintKanbanTasksCount,
            }
          : {
              planning: planningTasksCount,
              sprint: sprintKanbanTasksCount,
            }
      );
    }
  }, [tasksQuery.data]);

  useEffect(() => {
    setSelectedFramework(space?.framework || WorkspaceFramework.scrum);
    setSelectedDefaultModule(space?.defaultModule || WorkspaceDefaultModule.backlog);
    setisTaskMigrationMode(false);
  }, [space]);

  useEffect(() => {
    setisTaskMigrationMode(space?.framework !== selectedFramework);
  }, [space, selectedFramework]);

  return (
    <div className="w-full h-full px-8 flex flex-col items-start justify-between">
      <div className="flex flex-col items-start gap-2 mt-6">
        <div className="text-brand-grey-dark text-base font-semibold">Framework</div>

        <div className="flex gap-2 mt-4">
          <FrameworkCard
            framework={WorkspaceFramework.scrum}
            selected={selectedFramework === WorkspaceFramework.scrum}
            current={space?.framework === WorkspaceFramework.scrum}
            onSelected={() => handleUpdateFramework(WorkspaceFramework.scrum)}
            defaultModule={selectedFramework === WorkspaceFramework.scrum ? selectedDefaultModule : undefined}
            onDefaultModuleChange={handleDefaultModuleChange}
            defaultModuleOptions={getDefaultModuleOptions(WorkspaceFramework.scrum)}
          />

          <FrameworkCard
            framework={WorkspaceFramework.kanban}
            selected={selectedFramework === WorkspaceFramework.kanban}
            current={space?.framework === WorkspaceFramework.kanban}
            onSelected={() => handleUpdateFramework(WorkspaceFramework.kanban)}
            defaultModule={selectedFramework === WorkspaceFramework.kanban ? selectedDefaultModule : undefined}
            onDefaultModuleChange={handleDefaultModuleChange}
            defaultModuleOptions={getDefaultModuleOptions(WorkspaceFramework.kanban)}
          />
        </div>
        <FrameworkCard
          framework={WorkspaceFramework.none}
          selected={selectedFramework === WorkspaceFramework.none}
          current={space?.framework === WorkspaceFramework.none}
          onSelected={() => handleUpdateFramework(WorkspaceFramework.none)}
        />

        {isTaskMigrationMode && (
          <div className="h-full flex flex-col items-start">
            <Divider className="w-full" />
            <SpaceTasksMigration
              selectedFramework={selectedFramework}
              fromFramework={space?.framework || WorkspaceFramework.scrum}
              statusesMapping={statusesMapping}
              taskStatusCount={statusesTaskCount}
              setStatusesMapping={setStatusesMapping}
            />
          </div>
        )}
      </div>

      {isTaskMigrationMode && (
        <div className="w-full flex justify-between items-center gap-2 mb-3">
          <div className="flex justify-start items-center gap-1">
            {!(selectedFramework === WorkspaceFramework.none && space?.framework === WorkspaceFramework.kanban) && (
              <InfoCircleFilled className="text-neutral-800 mr-[4px]" />
            )}
            <div className="w-80 text-left text-neutral-900 text-xs font-normal leading-none">
              {selectedFramework === WorkspaceFramework.none
                ? space?.framework === WorkspaceFramework.scrum
                  ? 'Switching to no framework will end your active Sprint.'
                  : ''
                : selectedFramework === WorkspaceFramework.kanban
                  ? 'Switching to Kanban will end your active Sprint..'
                  : 'If moving tasks from Kanban to Sprint, a new sprint will be automatically created.'}
            </div>
          </div>
          <BaseButton type="primary" size="medium" onClick={() => setShowSwitchFrameworkModal(true)}>
            Switch Framework
          </BaseButton>
        </div>
      )}

      {!isTaskMigrationMode && isDefaultModuleChanged && (
        <div className="w-full flex justify-end items-center mb-3">
          <BaseButton type="primary" size="medium" onClick={() => onSave(selectedFramework, {}, selectedDefaultModule)}>
            Save
          </BaseButton>
        </div>
      )}

      {!isTaskMigrationMode && !isDefaultModuleChanged && (
        <div className="w-full flex justify-end items-center mb-3">
          <BaseButton type="primary" size="medium" disabled onClick={() => {}}>
            Switch Framework
          </BaseButton>
        </div>
      )}

      {isDisabled && (
        <CustomTooltip title={'You cannot change the teamspace framework'}>
          <div className="absolute top-[80px] left-[250px] w-[calc(100%-250px)] h-[calc(100%-80px)] bg-white/30 z-10 cursor-not-allowed pointer-events-auto" />
        </CustomTooltip>
      )}

      <Modal
        title={
          <div className="text-brand-grey-dark text-base font-semibold">
            Are you sure you want to switch the framework?
          </div>
        }
        open={showSwitchFrameworkModal}
        onOk={() => setShowSwitchFrameworkModal(false)}
        onCancel={() => setShowSwitchFrameworkModal(false)}
        centered
        footer={
          <div className="flex items-center justify-end gap-2">
            <BaseButton type="secondary" size="medium" onClick={() => setShowSwitchFrameworkModal(false)}>
              Cancel
            </BaseButton>
            <BaseButton
              type="primary"
              size="medium"
              onClick={() => {
                setShowSwitchFrameworkModal(false);
                onSave(selectedFramework, statusesMapping, selectedDefaultModule);
              }}
            >
              {`Yes, switch to ${getWorkspaceFrameworkDisplayName(selectedFramework)} framework`}
            </BaseButton>
          </div>
        }
      >
        <div className="text-brand-grey-dark text-sm font-normal mt-6 mb-10">
          {(() => {
            if (!space?.framework || !selectedFramework) return null;

            const descriptions = {
              scrum: {
                kanban:
                  'Sprint framework fundamentally differs from Kanban by having Planning and Sprint phases within its workflow. It is most used by teams who plan their work in advance.',
                none: 'Deciding to go without a framework will remove your ability to track tasks within Planning and Sprint phases. If you proceed, all your epics and tasks will be tracked within a single Backlog page.',
              },
              kanban: {
                scrum:
                  'Sprint framework fundamentally differs from Kanban by having Planning and Sprint phases within its workflow. It is most used by teams who plan their work in advance.',
                none: 'Deciding to go without a framework will remove your ability to track tasks within Kanban phase. If you proceed, all your epics and tasks will be tracked within a single Backlog page.',
              },
              none: {
                scrum:
                  'Sprint framework fundamentally differs from having no framework by having Planning and Sprint phases within its workflow. It is most used by teams who plan their work in advance.',
                kanban:
                  'Kanban framework differs from having no framework by allowing you to track and store ongoing tasks separately from your backlog. It is most used by teams who need to track continuous task delivery.',
              },
            } as Record<WorkspaceFramework, Partial<Record<WorkspaceFramework, string>>>;

            return descriptions[space.framework]?.[selectedFramework];
          })()}
        </div>
      </Modal>
    </div>
  );
};
